import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import {
  DEFAULT_DASHBOARD_FILTERS,
  DEFAULT_DASHBOARD_LAYOUT,
  getDashboardLayoutSections,
  normalizeDashboardLayout,
} from "./dashboardLayoutConfig";
import DashboardFiltersPanel from "./DashboardFiltersPanel";

const CONFIG_URL = (clientId) =>
  `https://adyogiadsapi.adyogi.com/client/${clientId}/custom-config?key=amz_dashboard_config`;

async function readLayoutConfig(clientId) {
  const response = await fetch(CONFIG_URL(clientId));
  const result = await response.json();
  if (!response.ok || result?.message) return null;
  return result;
}

async function saveLayoutOrder(clientId, layoutOrder) {
  const existing = await readLayoutConfig(clientId);
  const nextValue = {
    ...(existing?.value || {}),
    overview_metrices: existing?.value?.overview_metrices || [
      "impressions",
      "clicks",
      "orders",
      "sale_value",
    ],
    layout_order: ["overview", ...layoutOrder],
  };

  const layoutId = existing?.id || JSON.parse(localStorage.getItem("layoutId") || "null");
  const response = await fetch(
    layoutId
      ? `https://adyogiadsapi.adyogi.com/client/${clientId}/custom-config/${layoutId}`
      : `https://adyogiadsapi.adyogi.com/client/${clientId}/custom-config`,
    {
      method: layoutId ? "PUT" : "POST",
      headers: { "Content-type": "application/json" },
      body: JSON.stringify({
        ...(layoutId ? { id: layoutId } : {}),
        key: "amz_dashboard_config",
        value: nextValue,
      }),
    }
  );
  const result = await response.json();
  if (result?.id) {
    localStorage.setItem("layoutId", JSON.stringify(result.id));
  }
  if (!response.ok || result?.success === false) {
    throw new Error(result?.message || "Failed to save dashboard layout");
  }
  return result;
}

export default function DashboardLayoutDialog({
  open,
  onClose,
  filters,
  onFiltersChange,
  onResetFilters,
}) {
  const {
    clientId,
    dashboardLayoutSequence,
    setDashboardLayoutSequence,
    setItemList,
    setoverlayoutput,
    overlayoutput,
  } = useGlobalContext();
  const clientType = JSON.parse(localStorage.getItem("client_account_type"));
  const sections = getDashboardLayoutSections(clientType);
  const sectionLabel = Object.fromEntries(
    sections.map((section) => [section.id, section.label])
  );
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("layout");

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeDashboardLayout(dashboardLayoutSequence, clientType));
  }, [open, dashboardLayoutSequence, clientType]);

  const handleDrop = (result) => {
    if (!result.destination) return;
    setDraft((current) => {
      const next = [...current];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const applyOrder = (order) => {
    const normalized = normalizeDashboardLayout(order, clientType);
    setDashboardLayoutSequence(normalized);
    setItemList(normalized);
    setoverlayoutput(!overlayoutput);
  };

  const persist = async (order) => {
    if (!clientId || saving) return;
    try {
      setSaving(true);
      const normalized = normalizeDashboardLayout(order, clientType);
      await saveLayoutOrder(clientId, normalized);
      applyOrder(normalized);
      onClose();
    } catch (error) {
      toast.error(error?.message || "Failed to save dashboard layout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "visible" } }}
    >
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: "18px" }}>
          Set Dashboard Layout
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Drag sections to match the order on your Amazon dashboard. This
          layout is saved for the next time you log in. Filters apply to the
          dashboard immediately.
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeTab}
          onChange={(_, value) => value && setActiveTab(value)}
          sx={{ mt: 2 }}
        >
          <ToggleButton value="layout" sx={{ textTransform: "none", px: 2 }}>
            Layout
          </ToggleButton>
          <ToggleButton value="filters" sx={{ textTransform: "none", px: 2 }}>
            Filters
          </ToggleButton>
        </ToggleButtonGroup>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, overflow: activeTab === "layout" ? "visible" : "auto" }}>
        {activeTab === "filters" ? (
          <DashboardFiltersPanel
            filters={filters || DEFAULT_DASHBOARD_FILTERS}
            onChange={onFiltersChange}
            showProductSummary={clientType === "seller"}
          />
        ) : (
        <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1.25,
            border: "1px solid #E0E0E0",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            bgcolor: "#FAFAFA",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Always shown above the other sections
            </Typography>
          </Box>
        </Box>
        <DragDropContext onDragEnd={handleDrop}>
          <Droppable droppableId="amazon-dashboard-layout">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: "0 0 8px 8px",
                  overflow: "hidden",
                }}
              >
                {draft.map((id, index) => (
                  <Draggable key={id} draggableId={id} index={index}>
                    {(dragProvided, snapshot) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1.25,
                          borderTop: index === 0 ? "none" : "1px solid #E0E0E0",
                          bgcolor: snapshot.isDragging ? "#F4F1FF" : "#fff",
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: "text.secondary" }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                          {sectionLabel[id] || id}
                        </Typography>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="outlined"
          disabled={saving}
          onClick={() => {
            if (activeTab === "filters") onResetFilters?.();
            else persist(DEFAULT_DASHBOARD_LAYOUT);
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => {
            if (activeTab === "filters") onClose();
            else persist(draft);
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
